import { faSpinner, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import DeleteStoppageImageButton from "./DeleteStoppageImageButton";
import { StoppageDto } from "../../../DTOs/Stoppage/StoppageDto";
import { StoppageImageDTO } from "../../../DTOs/Stoppage/StoppageImageDTO";
import { stoppagesAjax } from "../../../ajax/StoppagesAjax";
import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { useCanWrite, useIsReadOnly } from "../../../utilities/helpers/roleUtilities";
import i18n from "../../../i18n";

type StoppageImagesModalProps = {
  stoppage: StoppageDto;
  onStoppageUpdated?: (updated: StoppageDto) => void;
}

const StoppageImagesModal: React.FC<StoppageImagesModalProps> = ({ stoppage, onStoppageUpdated }) => {
  const { t } = useTranslation("stoppages");
  const isReadOnly = useIsReadOnly();
  const canUpload = useCanWrite("Stoppages.UploadImage");
  const canDelete = useCanWrite("Stoppages.DeleteImage");
  const { enqueueSnackbar } = useSnackbar();
  const { hideModal } = useGlobalModal();
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);

  const { data: images = [], isFetching: isWorking, refetch: fetchImages } = useQuery({
    queryKey: ['stoppages', 'images', stoppage.stoppageId],
    queryFn: async () => {
      const imagesResponse = await stoppagesAjax.getStoppageImages(stoppage.stoppageId!);
      if (ErrorResponse.isErrorResponse(imagesResponse)) return [] as StoppageImageDTO[];
      return imagesResponse.data;
    },
    enabled: !!stoppage.stoppageId,
  });

  const handleFileUpload = useCallback(async (stoppage: StoppageDto, file?: File) => {
    if (!file || !stoppage.stoppageId) {
      enqueueSnackbar(t("messages.invalidStoppage"), { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await stoppagesAjax.uploadStoppageImage(stoppage.stoppageId, formData);

      if (!response.success || !response.data) {
        enqueueSnackbar(t("images.uploadFailed"), { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      onStoppageUpdated?.(response.data);
      enqueueSnackbar(t("images.uploaded"), { variant: 'success', autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar(t("images.uploadFailed"), { variant: 'error', autoHideDuration: 3000 });
    }
  }, [onStoppageUpdated, enqueueSnackbar, t]);

  const handleFileDelete = useCallback(async (imageId: number) => {
    if (!imageId) {
      enqueueSnackbar(t("images.invalidImage"), { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      const response = await stoppagesAjax.deleteStoppageImage(imageId);

      if (!response.success || !response.data) {
        enqueueSnackbar(t("images.deleteFailed"), { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      onStoppageUpdated?.(response.data);
      enqueueSnackbar(t("images.deleted"), { variant: 'success', autoHideDuration: 3000 });    } catch (error) {
      enqueueSnackbar(t("images.deleteFailed"), { variant: 'error', autoHideDuration: 3000 });
    }
  }, [onStoppageUpdated, enqueueSnackbar, t]);


  return (
    <div>
      <div className="flex flex-col items-center">
        <h2 className="border-b border-theme-heading-border font-semibold pb-2 mb-8 compact:mb-4 text-[1.6rem] compact:text-[1.2rem] text-theme-text">
          {t("images.title")}
        </h2>
        <div
          className="h-[32rem] relative w-full flex items-center justify-center border border-theme-heading-border rounded">
          <div
            className={`absolute inset-0 flex items-center justify-center bg-theme-surface-2 transition-opacity duration-300 ${isWorking ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none'}`}
          >
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="text-theme-text text-8xl"
            />
          </div>

          <div
            className={`bg-theme-surface-2 transition-opacity duration-300 w-full h-full ${isWorking ? 'opacity-0' : 'opacity-100'}`}>
            {images.length === 0 ?
              <div
                className="flex flex-col gap-4 items-center justify-center h-full w-full text-theme-text text-lg">
                <span className="font-semibold text-2xl">{t("images.none")}</span>
                {canUpload && <span>{t("images.upload")}</span>}
                {canUpload && <Button
                  variant="outlined"
                  component="label"
                  classes={{ root: 'h-[3rem] w-[10rem] flex items-center gap-2 !bg-theme-surface' }}
                  size="small"
                  sx={{ padding: 0.8 }}
                  disabled={isFileUploading || isReadOnly}
                >
                  <span className="absolute flex items-center justify-center text-theme-text">
                    <FontAwesomeIcon
                      icon={faUpload}
                      className={`text-lg transition-opacity duration-300 ease-in-out ${isFileUploading ? 'opacity-0' : 'opacity-100'}`}
                    />
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      className={`text-lg absolute left-0 transition-opacity duration-300 ease-in-out ${isFileUploading ? 'opacity-100' : 'opacity-0'}`}
                    />
                  </span>
                  <input
                    type="file"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setIsFileUploading(true);
                        await handleFileUpload(stoppage, file);
                        setIsFileUploading(false);
                        await fetchImages();
                      }
                    }} />
                </Button>}
              </div>
              :
              <ImageList
                className="w-full h-full overflow-auto"
                cols={images.length > 1 ? 2 : 1}
              >
                {images.map(image => (
                  <ImageListItem
                    key={image.stoppageImageId}
                    className="flex items-center gap-4 shadow-md"
                  >
                    <img
                      src={`data:${image.contentType};base64,${image.imageData}`}
                      alt={image.fileName}
                      className="min-h-[18rem] rounded"
                    />
                    <ImageListItemBar
                      sx={{
                        background:
                          'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                          'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                      }}
                      title=""
                      position="top"
                      actionIcon={canDelete &&
                        <DeleteStoppageImageButton
                          fileName={image.fileName || ''}
                          disabled={isReadOnly}
                          handleFileDelete={async () => {
                            await handleFileDelete(image.stoppageImageId);
                            await fetchImages();
                          }}
                        />
                      }
                      actionPosition="left"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            }
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-12 compact:mt-4">
        {canUpload && images.length !== 0 && <Button
          variant="outlined"
          component="label"
          classes={{ root: 'h-[3rem] w-[10rem] flex items-center gap-2 !bg-theme-surface' }}
          size="small"
          sx={{ padding: 0.8 }}
          disabled={isFileUploading || isReadOnly}
        >
          <span className="absolute flex items-center justify-center text-theme-text">
            <FontAwesomeIcon
              icon={faUpload}
              className={`text-lg transition-opacity ease-in-out ${isFileUploading ? 'opacity-0' : 'opacity-100'}`}
            />
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className={`text-lg absolute left-0 transition-opacity ease-in-out ${isFileUploading ? 'opacity-100' : 'opacity-0'}`}
            />
          </span>
          <input
            type="file"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                setIsFileUploading(true);
                await handleFileUpload(stoppage, file);
                setIsFileUploading(false);
                await fetchImages();
              }
            }} />
        </Button>
        }
        <button
          type="button"
          onClick={hideModal}
          className="px-4 py-2 bg-theme-text-muted text-white cursor-pointer hover:bg-theme-text-muted-hover hover:text-theme-text-hover transition ease-out rounded-sm border-none"
        >
          {i18n.t("actions.cancel")}
        </button>
      </div>
    </div>
  );
}

export default StoppageImagesModal;